import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaMoneyBillAlt, FaCalendar } from 'react-icons/fa';
import { despesasAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import DespesaModal from './DespesaModal';
import './Despesas.css';

const DespesasList = () => {
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDespesa, setEditingDespesa] = useState(null);
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [totalDespesas, setTotalDespesas] = useState(0);
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadDespesas();
  }, [dateFilter]);

  const loadDespesas = async () => {
    try {
      setLoading(true);
      const { data } = await despesasAPI.getAll(dateFilter);
      setDespesas(data.data);
      
      // Get total
      const totalRes = await despesasAPI.getTotal(dateFilter);
      setTotalDespesas(totalRes.data.data.total);
    } catch (error) {
      toast.error('Erro ao carregar despesas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, prestador) => {
    if (!window.confirm(`Deseja realmente excluir a despesa "${prestador}"?`)) {
      return;
    }

    try {
      await despesasAPI.delete(id);
      toast.success('Despesa excluída com sucesso!');
      loadDespesas();
    } catch (error) {
      toast.error('Erro ao excluir despesa');
      console.error(error);
    }
  };

  const handleEdit = (despesa) => {
    setEditingDespesa(despesa);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDespesa(null);
  };

  const handleSaveSuccess = () => {
    loadDespesas();
    handleCloseModal();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="despesas-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando despesas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="despesas-container">
      <div className="page-header">
        <div>
          <h1><FaMoneyBillAlt /> Gestão de Despesas</h1>
          <p>Controle de despesas e saídas</p>
        </div>
        {hasPermission('pode_inserir') && (
          <button onClick={() => setShowModal(true)} className="btn btn-primary">
            <FaPlus /> Nova Despesa
          </button>
        )}
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">Total do Período</span>
          <span className="stat-value negative">{formatCurrency(totalDespesas)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Quantidade</span>
          <span className="stat-value">{despesas.length} lançamento(s)</span>
        </div>
      </div>

      <div className="filters-card">
        <div className="filter-group">
          <FaCalendar />
          <label>Período:</label>
          <input
            type="date"
            value={dateFilter.startDate}
            onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
          />
          <span>até</span>
          <input
            type="date"
            value={dateFilter.endDate}
            onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
          />
        </div>
      </div>

      <div className="table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Prestador</th>
              <th>Histórico</th>
              <th>Valor</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {despesas.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  Nenhuma despesa encontrada no período
                </td>
              </tr>
            ) : (
              despesas.map((despesa) => (
                <tr key={despesa.id}>
                  <td>{formatDate(despesa.data_despesa)}</td>
                  <td><strong>{despesa.prestador}</strong></td>
                  <td>{despesa.historico || '-'}</td>
                  <td>
                    <span className="value-negative">{formatCurrency(despesa.valor)}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {hasPermission('pode_alterar') && (
                        <button
                          onClick={() => handleEdit(despesa)}
                          className="btn-icon btn-warning"
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                      )}
                      {hasPermission('pode_deletar') && (
                        <button
                          onClick={() => handleDelete(despesa.id, despesa.prestador)}
                          className="btn-icon btn-danger"
                          title="Excluir"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan="3"><strong>Total</strong></td>
              <td><strong className="value-negative">{formatCurrency(totalDespesas)}</strong></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {showModal && (
        <DespesaModal
          despesa={editingDespesa}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
        />
      )}
    </div>
  );
};

export default DespesasList;
